import { useState, useRef, useEffect } from "react";
import { HelpCircle, Info } from "lucide-react";

/**
 * Tooltip component - shows content on hover/focus
 * @param {Object} props
 * @param {React.ReactNode} props.content - Content to show in tooltip
 * @param {React.ReactNode} props.children - Trigger element (optional, defaults to ? icon)
 * @param {string} props.position - Tooltip position: 'top', 'bottom', 'left', 'right'
 * @param {string} props.className - Additional classes for tooltip
 * @param {boolean} props.useInfoIcon - Use Info icon instead of HelpCircle
 * @param {string} props.iconClassName - Classes for the icon
 * @param {number} props.maxWidth - Max width of tooltip in pixels
 */
export function Tooltip({
  content,
  children,
  position = "top",
  className = "",
  useInfoIcon = false,
  iconClassName = "",
  triggerClassName = "",
  maxWidth = 280,
  delay = 120,
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const triggerRef = useRef(null);
  const showTimer = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => () => clearTimeout(showTimer.current), []);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const trigger = triggerRef.current.getBoundingClientRect();
      const tooltip = tooltipRef.current.getBoundingClientRect();
      const gap = 8;

      let x = 0;
      let y = 0;

      switch (position) {
        case "top":
          x = trigger.left + trigger.width / 2 - tooltip.width / 2;
          y = trigger.top - tooltip.height - gap;
          break;
        case "bottom":
          x = trigger.left + trigger.width / 2 - tooltip.width / 2;
          y = trigger.bottom + gap;
          break;
        case "left":
          x = trigger.left - tooltip.width - gap;
          y = trigger.top + trigger.height / 2 - tooltip.height / 2;
          break;
        case "right":
          x = trigger.right + gap;
          y = trigger.top + trigger.height / 2 - tooltip.height / 2;
          break;
        default:
          x = trigger.left + trigger.width / 2 - tooltip.width / 2;
          y = trigger.top - tooltip.height - gap;
      }

      // Keep tooltip in viewport
      const padding = 8;
      x = Math.max(padding, Math.min(x, window.innerWidth - tooltip.width - padding));
      y = Math.max(padding, Math.min(y, window.innerHeight - tooltip.height - padding));

      setCoords({ x, y });
    }
  }, [isVisible, position]);

  // A short open delay keeps dense tables from flickering as the pointer crosses.
  const show = () => {
    clearTimeout(showTimer.current);
    showTimer.current = setTimeout(() => setIsVisible(true), delay);
  };
  const hide = () => {
    clearTimeout(showTimer.current);
    setIsVisible(false);
  };

  if (!content) return children || null;

  const Icon = useInfoIcon ? Info : HelpCircle;

  return (
    <>
      <div
        ref={triggerRef}
        className={`inline-flex items-center ${triggerClassName}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children || (
          <Icon
            className={`h-3.5 w-3.5 cursor-help text-low transition-colors hover:text-cyan-400 ${iconClassName}`}
          />
        )}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          role="tooltip"
          style={{
            position: "fixed",
            left: coords.x || 0,
            top: coords.y || 0,
            maxWidth: `${maxWidth}px`,
            zIndex: 9999,
          }}
          className={`pointer-events-none animate-fade-in rounded-xl border border-line-strong bg-surface px-3 py-2 text-xs leading-relaxed text-mid shadow-pop ${className}`}
        >
          {content}
          <div
            className="absolute h-2 w-2 rotate-45 border-b border-r border-line-strong bg-surface"
            style={{
              ...getArrowStyle(position),
            }}
          />
        </div>
      )}
    </>
  );
}

function getArrowStyle(position) {
  switch (position) {
    case "top":
      return { bottom: "-5px", left: "50%", transform: "translateX(-50%) rotate(45deg)" };
    case "bottom":
      return { top: "-5px", left: "50%", transform: "translateX(-50%) rotate(-135deg)" };
    case "left":
      return { right: "-5px", top: "50%", transform: "translateY(-50%) rotate(-45deg)" };
    case "right":
      return { left: "-5px", top: "50%", transform: "translateY(-50%) rotate(135deg)" };
    default:
      return { bottom: "-5px", left: "50%", transform: "translateX(-50%) rotate(45deg)" };
  }
}

/**
 * HelpIcon - A simple help icon with tooltip
 * @param {Object} props
 * @param {string} props.text - Tooltip text
 * @param {string} props.className - Additional classes
 */
export function HelpIcon({ text, className = "" }) {
  if (!text) return null;
  return (
    <Tooltip content={text} className={className}>
      <HelpCircle className="h-3.5 w-3.5 cursor-help text-low transition-colors hover:text-cyan-400" />
    </Tooltip>
  );
}

export default Tooltip;
